<script setup>
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';

const props = defineProps({
  data: Object
});

const { getGridImageContent } = useGridImageContentData();
const { data: gridImageContent } = await getGridImageContent();

let gridImageContentData = null;
if (props.data.override) {
  gridImageContentData = gridImageContent.value.gridImageContent;
} else {
  gridImageContentData = props.data.carouselContent;
}
const options = {
  perPage: 1,
  perMove: 0.75,
  pagination: false,
  type: 'Slide',
  speed: 500,
  gap: '20px',
  arrows: false,
  mediaQuery: 'min',
};
const isMobile = ref(false);
const getImageAltWithFallback = (image) =>
  resolveMediaAlt(image);

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  handleResize();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>
  
<template>
  <div class="block grid-image-content">
    <div class="container">
      <div class="row justify-content-center">
        <template v-if="isMobile">
          <Splide :options="options">
            <SplideSlide v-for="(data, index) in gridImageContentData" :key="index">
              <div class="col">
                <div class="row">
                  <div class="col-6">
                    <!-- Mobile image -->
                    <NuxtImg v-if="data.mobileImage"
                      :src="data?.mobileImage?.url || 'https://via.placeholder.com/360x360'" 
                      :alt="getImageAltWithFallback(data?.mobileImage)"
                      loading="lazy"
                      decoding="async"
                      format="webp"
                      :class="data.mobileImage ? 'd-lg-none' : ''"
                      aria-hidden="true" />

                      <!-- Desktop image -->
                    <NuxtImg 
                      :src="data?.image?.url || 'https://via.placeholder.com/360x360'" 
                      :alt="getImageAltWithFallback(data?.image)" 
                      loading="lazy"
                      decoding="async"
                      format="webp"
                      :class="data.mobileImage ? 'd-none d-lg-block' : ''"  
                      aria-hidden="true"
                    />
                  </div>
    
                  <div class="col-6">
                    <NuxtImg v-if="data.brandImage"
                      :src="data?.brandImage?.url" 
                      :alt="getImageAltWithFallback(data?.brandImage)"
                      loading="lazy"
                      decoding="async"
                      aria-hidden="true" />
                  </div>
                </div>
                <div class="row py-3">
                  <div class="col">
                    <div class="personal-info pb-3">
                      <div v-html="data.personalInfo"></div>
                    </div>
                    <div v-html="data.content"></div>
                  </div>
                </div>
              </div>
            </SplideSlide>
          </Splide>
        </template>
        <template v-else>
          <div v-for="(data, index) in gridImageContentData" :key="index" class="col-md-6">
            <div class="row">
              <div class="col-md-2">
                <NuxtImg 
                  :src="data?.image?.url || 'https://via.placeholder.com/360x360'" 
                  :alt="getImageAltWithFallback(data?.image)"
                  loading="lazy"
                  decoding="async"
                  format="webp"
                  aria-hidden="true" />
              </div>
              <div class="col-md-7 personal-info">
                <div v-html="data.personalInfo"></div>
              </div>
              <div class="col-md-3">
                <NuxtImg v-if="data.brandImage"
                  :src="data?.brandImage?.url" 
                  :alt="getImageAltWithFallback(data?.brandImage)"
                  loading="lazy"
                  decoding="async"
                  aria-hidden="true" />
              </div>
            </div>
            <div class="row py-3">
              <div class="col">
                <div v-html="data.content"></div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .grid-image-content {
    @include media-breakpoint-down(md) {
      .splide__slide {
      width: 80% !important; /* Adjust percentage to simulate 1.25 slides visible */
    }
  }
    .personal-info {
      p {
        margin-bottom: 0;
        &:last-child {
          font-size: 12px;
        }
      }
    }
  }
</style>